import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
    Breadcrumb,
    Layout,
    Spin,
    Table,
    Button, Space, Input, Card, Row, Col,
} from 'antd';
import axios from 'axios';
import { SearchOutlined, ClearOutlined } from '@ant-design/icons';
import Highlighter from 'react-highlight-words';
import { APPBACKGROUND_COLOR, MAINAPIURL } from '../../appsettings';
import { AlertError } from '../../usableclasses';
import Headerdiv from '../../layout/header';
import Footerdiv from '../../layout/footer';

const { Content } = Layout;

const PendingOrderProcesss = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState([]);
    const { id } = useParams();
    const ApiURL = MAINAPIURL;
    const TOKEN = localStorage.getItem("AUTHCODE");

    const [tableFilters, setTableFilters] = useState({});
    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef(null);

    useEffect(() => {
        fetchOrders();
    }, []);

    const fetchOrders = async () => {
        try {
            setLoading(true);
            const headers = {
                SystemAccessToken: TOKEN
            };
            const response = await axios.get(`${ApiURL}/orders/pendingpointsprocess`, { headers });
            setData(response.data);
        } catch (error) {
            AlertError("Failed to fetch order: " + (error.message || "Unknown error"));
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (selectedKeys, confirm, dataIndex, closeDropdown = true) => {
        confirm({ closeDropdown });
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
        setTableFilters(prev => ({
            ...prev,
            [dataIndex]: selectedKeys[0] ? [selectedKeys[0]] : null // Store as array for consistency with AntD filters
        }));
    };

    const getColumnSearchProps = (dataIndex, title) => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${title}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => {
                            clearFilters(); // Clears internal AntD filter state for this column
                            setSearchText(''); // Clear local search text state
                            setSearchedColumn(''); // Clear local searched column state
                            setTableFilters(prev => { // Update the global tableFilters state for this column
                                const newFilters = { ...prev };
                                delete newFilters[dataIndex];
                                return newFilters;
                            });
                            confirm({ closeDropdown: true }); // Close dropdown and apply changes
                        }}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button type="link" size="small" onClick={() => { close(); }}>
                        Close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        onFilter: (value, record) => record[dataIndex] ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()) : '',
        onFilterDropdownOpenChange: (visible) => { if (visible) { setTimeout(() => searchInput.current?.select(), 100); } },
        render: (text) => searchedColumn === dataIndex ? (<Highlighter highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }} searchWords={[searchText]} autoEscape textToHighlight={text ? text.toString() : ''} />) : (text),
        filteredValue: tableFilters[dataIndex] || null, // Control filter value
    });

    const handleTableChange = (pagination, filters, sorter) => {
        setTableFilters(filters);
    };

    const handleClearAllTableFilters = () => {
        setTableFilters({});
        setSearchText('');
        setSearchedColumn('');
    };

    const columns = [
        {
            title: 'Invoice Ref',
            dataIndex: 'invoiceRef',
            key: 'invoiceRef',
            ...getColumnSearchProps('invoiceRef', 'Invoice Ref'),
        },
        {
            title:"Process Type",
            dataIndex: 'processType',
            key: 'processType',
            render: (value) => value ? value : 'N/A',
        },
        {
            title: 'Order Placed Time',
            dataIndex: 'orderPlacedDateTime',
            key: 'orderPlacedDateTime',
        },
        {
            title: 'Consult ID',
            dataIndex: 'consultId',
            key: 'consultId',
            ...getColumnSearchProps('consultId', 'Consult ID'),
        },
        {
            title: 'System Year',
            dataIndex: 'sysYear',
            key: 'sysYear',
        },
        {
            title: 'System Month',
            dataIndex: 'sysMonth',
            key: 'sysMonth',
        },
        {
            title: 'Points',
            dataIndex: 'points',
            key: 'points',
        },
        {
            title: 'Is Started',
            dataIndex: 'isStart',
            key: 'isStart',
            render: (value) => value ? 'Yes' : 'No',
        },
        {
            title: 'Is Complete',
            dataIndex: 'isComplete',
            key: 'isComplete',
            render: (value) => value ? 'Yes' : 'No',
        },
        {
            title: 'Process Start',
            dataIndex: 'processStart',
            key: 'processStart',
        },
        {
            title: 'Process End',
            dataIndex: 'processEnd',
            key: 'processEnd',
        },
    ];

    return (
        <Spin spinning={loading} tip="Loading order details...">
            <Layout style={{ backgroundColor: APPBACKGROUND_COLOR }}>
                <Headerdiv />
                <Content style={{ padding: '10px' }}>
                    <Breadcrumb style={{ margin: '16px 0' }}>
                        <Breadcrumb.Item><Link to={'/orders'}>MLM Orders</Link></Breadcrumb.Item>
                        <Breadcrumb.Item>Pending Order Process</Breadcrumb.Item>
                    </Breadcrumb>

                    <Card style={{ marginBottom: 16 }}>
                        <Row gutter={[16, 16]} justify="end" align="middle">
                            <Col>
                                <Space wrap>
                                    <Button
                                        icon={<ClearOutlined />}
                                        onClick={handleClearAllTableFilters}
                                    >
                                        Clear All Filters
                                    </Button>
                                    <Button onClick={fetchOrders}>Refresh List</Button>
                                </Space>
                            </Col>
                        </Row>
                    </Card>

                    <Table
                        dataSource={data}
                        columns={columns}
                        rowKey="invoiceRef"
                        bordered
                        onChange={handleTableChange}
                    />
                </Content>
                <Footerdiv />
            </Layout>
        </Spin>
    );
};

export default PendingOrderProcesss;
